// entryState — the history ENTRY's own facts: how the person got to where they
// stand (ADR-036 §1 — the browser's history is the only stack; FRONTEND-PATTERNS
// §URL state). They ride the entry's own data — the router's location state: the
// browser keeps it across a refresh, Back and Forward, and it never travels in a
// shared link — each a KEY beside the others:
//   • the overlay's step (`overlayStep`) — the ONE history step an overlay held in
//     the link owns (the peek `?peek=`, the file viewer `?file=`, the import run
//     `?job=` — ADR-037 §2): opening one pushes an entry marked with which overlay
//     owns the step, the query it opened over (and the params its door closed — a
//     peek opened out of the file viewer; the step never changes the page's path,
//     so the query is the place) and the scroll memory of that place. A close
//     reads the mark and works exactly like Back — the step is given back — and a
//     change the person made to the page while the overlay was open is carried
//     down onto the page's own entry, so history ends as if the overlay had never
//     opened. An entry without a mark (a pasted link) was never this page's step:
//     its close replaces. The scroll restoration reads the same mark
//     (lib/scrollScope), so an overlay's step shares the scroll memory of the
//     place it opened over and opening one never moves the page;
//   • the way back (`overlayTrail`) — the records a link inside the overlay was
//     followed through, each with the name its back chevron says: a fresh open
//     starts it empty, a close leaves it with the entry it closes;
//   • the visit's start (`visitStart`) — the history index a page or a focused
//     flow was entered at, stamped by the surface on each of its own entries
//     (lib/historyEntry): its exit pops back to the entry before it in ONE move,
//     and two visits to one place never share it.
// Every write composes (`composeEntry` — one key set or taken off, every other
// key and the router's own index and key kept), and this module is the only one
// that writes the window's history: the one-shot hand-offs to the next page (a
// role's clone, the site setup's step …) are the only other history state — the
// tripwire (entryState.tripwire.test.ts) holds both.

/** The keys the entry's own facts ride under, beside each other. */
type EntryKey = 'overlayStep' | 'overlayTrail' | 'visitStart';

/** The entry state with `key` set to `value` — or taken off when `value` is undefined — every other key kept; null
 *  when nothing is left. The ONE composer: no fact overwrites another, whoever writes it. */
export function composeEntry(state: unknown, key: EntryKey, value: unknown): Record<string, unknown> | null {
  const { [key]: _gone, ...rest } = state && typeof state === 'object' ? (state as Record<string, unknown>) : {};
  const next = value === undefined ? rest : { ...rest, [key]: value };
  return Object.keys(next).length ? next : null;
}

/** The live entry's own state (the router keeps it under `usr`). */
export function liveState(): unknown {
  return (window.history.state as { usr?: unknown } | null)?.usr ?? null;
}

/** The live entry's history index — the router keeps it beside the entry's state (0 without one). */
export function currentIndex(): number {
  const idx = (window.history.state as { idx?: unknown } | null)?.idx;
  return typeof idx === 'number' ? idx : 0;
}

/** The router IS the address — a browser router, which keeps its index and the entry's key in the window's history
 *  state — and not a memory router (a story, a test), which never touches the window's history. */
export function routerIsAddress(routerKey: string): boolean {
  const entry = window.history.state as { idx?: unknown; key?: unknown } | null;
  return typeof entry?.idx === 'number' && (typeof entry.key === 'string' ? entry.key : 'default') === routerKey;
}

/** Rewrite the live entry's own state in place (and its address with it, when given) — the router's index and key
 *  kept. Nothing renders from it: no navigation. */
export function writeEntry(state: unknown, href: string = window.location.href): void {
  window.history.replaceState({ ...window.history.state, usr: state }, '', href);
}

/** Push an entry over the live one with its own `state` — where a memory router is no address (a story, a test),
 *  as nuqs would write it, the router left where it is. */
export function pushEntry(state: unknown, href: string): void {
  const entry = window.history.state as Record<string, unknown> | null;
  window.history.pushState(entry && { ...entry, usr: state }, '', href);
}

// ── the overlay's step ─────────────────────────────────────────────────────

export interface OverlayStep {
  /** The URL param the overlay lives in. */
  param: string;
  /** The query the overlay opened over (`queryOf`). */
  base: string;
  /** Params of that address the opening closed in the same move (a door out of a modal). */
  cleared: string[];
  /** The scroll-restoration key of the place under the overlay. */
  scroll: string;
}

/** The step mark an entry's state carries, or null — a plain entry, a deep link, a malformed state (tolerant reads). */
export function overlayStepOf(state: unknown): OverlayStep | null {
  const mark = (state as Record<string, unknown> | null | undefined)?.overlayStep;
  if (!mark || typeof mark !== 'object') return null;
  const { param, base, cleared, scroll } = mark as Record<string, unknown>;
  if (typeof param !== 'string' || typeof base !== 'string' || typeof scroll !== 'string') return null;
  return { param, base, scroll, cleared: Array.isArray(cleared) ? cleared.filter((c): c is string => typeof c === 'string') : [] };
}

/** The state of the entry the step's opening pushes — the mark alone (the surface under it stamps its own facts). */
export function withOverlayStep(step: OverlayStep): Record<string, unknown> | null {
  return composeEntry(null, 'overlayStep', step);
}

/** The entry state with the mark of `param` — and its way back — taken off: the close that replaces leaves an
 *  ordinary entry. */
export function withoutOverlayStep(state: unknown, param: string): unknown {
  const bare = withOverlayTrail(state, param, []);
  if (overlayStepOf(bare)?.param !== param) return bare;
  return composeEntry(bare, 'overlayStep', undefined);
}

/** A query string as a comparable string — its params in a fixed order (a write may reorder them), the `without`
 *  params dropped. */
export function queryOf(search: string, without: readonly string[] = []): string {
  const params = new URLSearchParams(search);
  for (const key of without) params.delete(key);
  params.sort();
  return params.toString();
}

/** The params the opening itself moved — the ones its door closed, and the overlay's own (a door out of a modal can
 *  open the overlay over itself: a product's file opening another product) — never the person's change. */
const openedBy = (step: OverlayStep): string[] => [...step.cleared, step.param];

/** Is the page under the overlay still as it stood when the overlay opened? */
export function placeUnchanged(step: OverlayStep, search: string): boolean {
  return queryOf(search, [step.param]) === queryOf(step.base, openedBy(step));
}

/** The address the entry under the overlay is to show when the page changed while it was open: the page as the
 *  person left it, and what the opening moved put back over it — as Back would have shown it. */
export function carriedAddress(step: OverlayStep, pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  params.delete(step.param);
  const base = new URLSearchParams(step.base);
  for (const key of openedBy(step)) for (const value of base.getAll(key)) params.append(key, value);
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

// ── the way back ───────────────────────────────────────────────────────────

/** A record a link inside the overlay was followed FROM — its value (the overlay's param) and the name its back
 *  chevron says ("Back to …"). */
export interface TrailRecord {
  value: string;
  label: string;
}

/** The way back keeps this many records — real chains run two to four deep; past it the oldest drops (X and the
 *  browser's Back still close the whole overlay). ONE number for every overlay held in the link. */
export const TRAIL_CAP = 5;

/** The way back an entry's state carries for `param` — the records followed through, oldest first (tolerant reads).
 *  It rides beside the step's mark, never inside it: a pasted link has no step of ours, yet a link followed inside
 *  it still keeps its way back. */
export function overlayTrailOf(state: unknown, param: string): TrailRecord[] {
  const trail = (state as Record<string, unknown> | null | undefined)?.overlayTrail as { param?: unknown; records?: unknown } | undefined;
  if (!trail || trail.param !== param || !Array.isArray(trail.records)) return [];
  return trail.records.filter(
    (r): r is TrailRecord => !!r && typeof (r as TrailRecord).value === 'string' && typeof (r as TrailRecord).label === 'string',
  );
}

/** The entry state with `param`'s way back set to `records` (capped — the oldest drop), or taken off when empty
 *  (another overlay's way back is left as it is). */
export function withOverlayTrail(state: unknown, param: string, records: readonly TrailRecord[]): unknown {
  if (records.length) return composeEntry(state, 'overlayTrail', { param, records: records.slice(-TRAIL_CAP) });
  if (!overlayTrailOf(state, param).length) return state;
  return composeEntry(state, 'overlayTrail', undefined);
}

// ── the visit's start ──────────────────────────────────────────────────────

/** The history index the visit to `scope` began at, as the entry's state carries it — null for an entry no surface
 *  of `scope` stamped (a deep link's first render, another place's entry, a malformed state). */
export function visitStartOf(state: unknown, scope: string): number | null {
  const start = (state as Record<string, unknown> | null | undefined)?.visitStart as { scope?: unknown; idx?: unknown } | undefined;
  if (!start || start.scope !== scope || typeof start.idx !== 'number' || !Number.isInteger(start.idx) || start.idx < 0) return null;
  return start.idx;
}

/** The entry state stamped with where the visit to `scope` began. */
export function withVisitStart(state: unknown, scope: string, idx: number): Record<string, unknown> | null {
  return composeEntry(state, 'visitStart', { scope, idx });
}
